import { createContext, useContext, useEffect, useState } from "react";
import { tr } from "./i18n.js";

/**
 * Prompt-only mode — hold Cmd (mac) or Ctrl (Win/Linux) to convert any
 * AI-triggering button into "save the prompt but don't run the LLM".
 *
 * Purpose (from user 2026-08-27): admins want to debug prompts by
 * seeing exactly what WOULD have been sent to Claude for research,
 * write, revise, fact-check, image-gen, upload-article commands, etc.
 *
 * Implementation:
 *   - This hook watches for keydown/keyup on Meta/Control and keeps a
 *     module-level flag `_held`. The flag is what the axios interceptor
 *     reads on every outgoing request to decide whether to attach
 *     `X-Prompt-Only: 1`.
 *   - A React state mirror (`held`) is exposed so components can
 *     re-render to their purple variant when the key goes down/up.
 *   - Window blur resets the flag — otherwise Alt-Tabbing away and
 *     back with the key still logically "down" would silently trip
 *     prompt-only for the next click.
 *   - Admin-only gating happens in `usePromptOnly()` — non-admins
 *     never enter prompt-only mode even if their keyboard fires the
 *     event.
 *
 * Backend contract:
 *   - Middleware in `server.py` reads `X-Prompt-Only: 1`, flips a
 *     ContextVar, and `_AnthropicChat.send_message()` raises
 *     `PromptOnlySignal` after logging the prompt but before hitting
 *     Anthropic. The exception handler returns 202 with
 *     `{prompt_only: true, message: "..."}`. See `prompt_mode.py`.
 *
 * Frontend consumers:
 *   - Any AI-triggering button should apply `promptOnlyClasses(held)`
 *     to its className. That swaps to the purple variant when held.
 *   - The success-toast site should check `response.prompt_only` and
 *     show the returned `message` instead of the normal success copy.
 */

let _held = false;
export function isPromptOnlyHeld() {
  return _held;
}

const PromptOnlyContext = createContext(false);

export function PromptOnlyProvider({ children, isAdmin }) {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      // Non-admins: never listen. Also make sure the module flag stays
      // false in case an admin session was replaced without a reload.
      _held = false;
      setHeld(false);
      return;
    }
    const onDown = (e) => {
      // Meta = Cmd on macOS; Control = Ctrl on Windows/Linux. Ignore
      // when typing into an input/textarea so text-editing shortcuts
      // like Cmd+A / Cmd+Z don't trigger the mode.
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        document.activeElement?.isContentEditable;
      if (isTyping) return;
      if (e.key === "Meta" || e.key === "Control") {
        _held = true;
        setHeld(true);
        document.body.setAttribute("data-prompt-only", "true");
      }
    };
    const onUp = (e) => {
      if (e.key === "Meta" || e.key === "Control") {
        _held = false;
        setHeld(false);
        document.body.removeAttribute("data-prompt-only");
      }
    };
    const onBlur = () => {
      _held = false;
      setHeld(false);
      document.body.removeAttribute("data-prompt-only");
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
      document.body.removeAttribute("data-prompt-only");
    };
  }, [isAdmin]);

  return (
    <PromptOnlyContext.Provider value={held}>
      {children}
      {held && (
        // Compact top-of-screen banner so admins can never miss when
        // they're in prompt-only mode. Purple to match the button
        // colour swap. Fixed-position so it doesn't push content.
        <div
          data-testid="prompt-only-banner"
          className="fixed top-0 left-0 right-0 z-[100] bg-purple-600 text-white text-center text-[11px] font-mono-display uppercase tracking-wider py-1 px-3 pointer-events-none"
          role="status"
        >{tr("Prompt-only mode active — clicks will save the prompt without running the LLM. Release {value} to cancel.", { value: navigator.platform.toLowerCase().includes("mac") ? "⌘" : tr("Ctrl") })}</div>
      )}
    </PromptOnlyContext.Provider>
  );
}

export function usePromptOnly() {
  return useContext(PromptOnlyContext);
}
